import { Button, makeStyles, Paper, Theme } from '@material-ui/core';
import { Entity } from '@moderepo/mode-apis';
import { BaseEntityField, BaseInfoCompActionsSet, BaseInfoCompSaveAction, BaseInfoCompProps, useModePanelStyle, useModeFormStyle, InfoCompPanelHeader,
    ModeInfoCompTextInputField, ModeInfoCompAutoCompleteInputField, inputStateSetterHelper, FontIcon, ModeInfoCompSelectInputField,
    usePropValue, ModeFormInputSection, BaseEntityFieldArray } from '@moderepo/mode-app-components';
import clsx from 'clsx';
import { useCallback } from 'react';
import { EntityInputComp, EntityInputField } from '..';
import { AlertsDashboardWidgetInputs, AlertsDashboardWidgetViewType } from '../../containers';



const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
        },

        hiddenColumnOptionsContainer: {
            display       : 'flex',
            flexDirection : 'column',
            alignItems    : 'flex-start',
            justifyContent: 'center',
            marginLeft    : theme.spacing(3),
            marginTop     : theme.spacing(2),
        },

        hiddenColumnOption: {
        },
    };
}, { name: 'AlertsDashboardWidgetSettingsComp', index: 1 });



/**
 * This component has a custom saveUpdate button's onClick input params therefore we need to extends the BaseInfoCompActionsSet and
 * override the 'saveUpdate' action.
 */
export interface AlertsDashboardWidgetActionsSet extends BaseInfoCompActionsSet {
    readonly saveUpdate?: BaseInfoCompSaveAction<AlertsDashboardWidgetInputs> | undefined
}



export interface AlertsDashboardWidgetSettingsCompProps extends BaseInfoCompProps {
    readonly fields: {
        readonly title: BaseEntityField<string>;
        readonly icon: BaseEntityField<string>;
        readonly homeId: BaseEntityField<number>;
        readonly entity: EntityInputField;
        readonly view: {
            readonly viewType: BaseEntityField<AlertsDashboardWidgetViewType>;
            readonly pageSize: BaseEntityField<number>;
            readonly hideColumns?: BaseEntityFieldArray<string>;
        };
    };

    readonly texts: {
        readonly viewSettings: string;
    };
    readonly actions?: AlertsDashboardWidgetActionsSet | undefined;
}



export const AlertsDashboardWidgetSettingsComp: React.FC<AlertsDashboardWidgetSettingsCompProps> = (
    props: AlertsDashboardWidgetSettingsCompProps,
) => {

    const [widgetTitleInput, setWidgetTitleInput] = usePropValue<string | undefined>(props.fields.title.value);
    const [widgetIconInput, setWidgetIconInput] = usePropValue<string | undefined>(props.fields.icon.value);
    const [homeIdInput, setHomeIdInput] = usePropValue<number | undefined>(props.fields.homeId.value);
    const [entityInput, setEntityInput] = usePropValue<Entity | undefined>(props.fields.entity?.value);
    const [viewTypeInput, setViewTypeInput] = usePropValue<AlertsDashboardWidgetViewType | undefined>(props.fields.view.viewType.value);
    const [pageSizeInput, setPageSizeInput] = usePropValue<number | undefined>(props.fields.view.pageSize.value);
    const [hideColumnsInputs, setHideColumnsInputs] = usePropValue<readonly string[] | undefined>(props.fields.view.hideColumns?.value);

    const panelClasses = useModePanelStyle();
    const formClasses = useModeFormStyle();
    const classes = useStyle();


    /**
     * On input change handler for all the inputs. When an input change, call the onInputChangeHelper and pass it the setter functions
     * for all inputs. The helper function will call the setter function accordingly based on the input name.
     */
    const onInputChange = useCallback((inputName: string, value: any) => {
        inputStateSetterHelper(inputName, value, {
            title   : setWidgetTitleInput,
            icon    : setWidgetIconInput,
            homeId  : setHomeIdInput,
            entity  : setEntityInput,
            viewType: setViewTypeInput,
            pageSize: setPageSizeInput,
        });
    }, [setWidgetTitleInput, setWidgetIconInput, setHomeIdInput, setEntityInput, setViewTypeInput, setPageSizeInput]);



    /**
     * On cancel edit, we need to reverse all the input back to the original values
     */
    const reverseFormInput = useCallback(() => {
        setWidgetTitleInput(props.fields.title.value);
        setWidgetIconInput(props.fields.icon.value);
        setHomeIdInput(props.fields.homeId.value);
        setEntityInput(props.fields.entity.value);
        setViewTypeInput(props.fields.view.viewType.value);
        setHideColumnsInputs(props.fields.view.hideColumns?.value);
        setPageSizeInput(props.fields.view.pageSize.value);
    }, [setWidgetTitleInput, props.fields.title.value, props.fields.icon.value, props.fields.homeId.value, props.fields.entity.value,
        props.fields.view.viewType.value, props.fields.view.hideColumns?.value, props.fields.view.pageSize.value, setWidgetIconInput, setHomeIdInput,
        setEntityInput, setViewTypeInput, setHideColumnsInputs, setPageSizeInput]);


    /**
     * On form submit. Collect the values for all input and pass it back to the container
     */
    const onFormSubmit = useCallback(async (event: React.FormEvent) => {
        event.stopPropagation();
        event.preventDefault();

        if (props.actions?.saveUpdate) {
            props.actions.saveUpdate.onClick({
                title      : widgetTitleInput?.trim(),
                icon       : widgetIconInput?.trim(),
                homeId     : homeIdInput ? Number(homeIdInput) : undefined,
                entity     : entityInput,
                viewType   : viewTypeInput,
                hideColumns: hideColumnsInputs,
                pageSize   : pageSizeInput ? Number(pageSizeInput) : undefined,
            });
        }

        return false;
    }, [entityInput, hideColumnsInputs, homeIdInput, props.actions?.saveUpdate, viewTypeInput, widgetIconInput, widgetTitleInput, pageSizeInput]);



    return (
        <Paper
            elevation={2}
            className={clsx(panelClasses.root, classes.root, props.className)}
        >
            <InfoCompPanelHeader {...props} />
            <div className={clsx(panelClasses.panelContent)}>
                <form
                    className={clsx(formClasses.root, props.formProps?.className)}
                    noValidate
                    autoComplete="on"
                    method="POST"
                    onSubmit={onFormSubmit}
                >
                    <ModeInfoCompTextInputField
                        className={formClasses.formInputField}
                        inputType="text"
                        compProps={props}
                        inputProps={props.fields.title}
                        fieldName="title"
                        inputValue={widgetTitleInput}
                        onInputChange={onInputChange}
                    />
                    <ModeInfoCompAutoCompleteInputField
                        className={formClasses.formInputField}
                        fieldName="icon"
                        compProps={props}
                        inputProps={props.fields.icon}
                        inputValue={widgetIconInput}
                        onInputChange={onInputChange}
                    />
                    <ModeInfoCompSelectInputField
                        className={formClasses.formInputField}
                        compProps={props}
                        inputProps={props.fields.homeId}
                        fieldName="homeId"
                        inputValue={homeIdInput}
                        onInputChange={onInputChange}
                    />
                    <EntityInputComp
                        className={formClasses.formInputField}
                        fieldName="entity"
                        isEditing={props.isEditing}
                        {...props.fields.entity}
                        value={entityInput}
                        onFieldClicked={props.onFieldClicked}
                        onFormInputChange={(fieldName: string, entity: Entity | undefined) => {
                            // Save the input locally
                            onInputChange(fieldName, entity);

                            // notify the container input changed
                            if (props.onFormInputChange) {
                                props.onFormInputChange(fieldName, entity);
                            }
                        }}
                    />

                    <ModeFormInputSection
                        collapsible
                        title={props.texts.viewSettings}
                        icon="visibility"
                    >
                        <ModeInfoCompSelectInputField
                            className={formClasses.formInputField}
                            compProps={props}
                            inputProps={props.fields.view.viewType}
                            fieldName="viewType"
                            inputValue={viewTypeInput}
                            onInputChange={onInputChange}
                        />
                        <ModeInfoCompSelectInputField
                            className={formClasses.formInputField}
                            compProps={props}
                            inputProps={props.fields.view.pageSize}
                            fieldName="pageSize"
                            inputValue={pageSizeInput}
                            onInputChange={onInputChange}
                        />
                        <ModeInfoCompAutoCompleteInputField
                            className={formClasses.formInputField}
                            fieldName="hideColumns"
                            compProps={props}
                            inputProps={props.fields.view.hideColumns}
                            inputValue={hideColumnsInputs}
                            onInputChange={onInputChange}
                        />
                    </ModeFormInputSection>


                    {props.isEditing && props.actions && (
                        <div className={clsx(formClasses.formActionContainer)}>
                            <div />
                            <div>
                                {props.actions?.cancelEdit && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        size="small"
                                        startIcon={props.actions.cancelEdit.icon
                                            ? <FontIcon iconName={props.actions.cancelEdit.icon} /> : undefined}
                                        onClick={() => {
                                            reverseFormInput();
                                            if (props.actions?.cancelEdit) {
                                                props.actions.cancelEdit.onClick();
                                            }
                                        }}
                                    >
                                        {props.actions.cancelEdit.label}
                                    </Button>
                                )}
                                {props.actions?.saveUpdate && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        size="small"
                                        startIcon={props.actions.saveUpdate.icon
                                            ? <FontIcon iconName={props.actions.saveUpdate.icon} /> : undefined}
                                    >
                                        {props.actions.saveUpdate.label}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </form>
            </div>
        </Paper>
    );
};
